import { Formik } from 'formik';
import { useEffect, useState } from 'react';

import { CategorySelector } from './CategorySelector';

import { CloseButton } from '@components/ActionButtons/CloseButton';
import { Box } from '@components/Box';
import {
  FormButtonsContainer,
  FormSectionContainer,
} from '@components/Container';
import {
  CancelButton,
  DeleteButton,
  FormikCheckbox,
  FormikSelectMultiple,
  SubmitButton,
} from '@components/Form';
import { FormikInput } from '@components/Form/FormikInput';
import { Loading } from '@components/Loading';
import { Space } from '@components/Space';
import { useNotification } from '@hooks/useNotification';
import { useProductGet } from '@hooks/useProductGet';
import { useSalesTaxSelectOptions } from '@hooks/useSalesTaxSelectOptions';
import { ReactSelectOption } from '@models/common/ReactSelectOption';
import { productValidation } from '@models/Product';
import { salesTaxDefaultValue } from '@models/SalesTax';
import { ProductService } from '@services/Product';

interface Props {
  productId?: number;
  onDelete?: () => void;
  onClose?: (refreshGrid?: boolean) => void;
}

export const ProductEdit: React.FC<Props> = ({
  productId,
  onClose,
  onDelete,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const { showError, showSuccess } = useNotification();

  const {
    product,
    isLoading: isProductLoading,
    isSuccess: isProductGetSuccess,
    loadData: loadProduct,
  } = useProductGet();

  const {
    salesTaxSelectOptions,
    isLoading: isSalesTaxSelectOptionsLoading,
    loadData: loadSalesTaxSelectOptions,
  } = useSalesTaxSelectOptions();

  const [productSalesTaxes, setProductSalesTaxes] =
    useState<ReactSelectOption[]>();

  const handleProductSalesTaxesOnChange = (
    option: readonly ReactSelectOption[]
  ) => {
    const selectedSalesTaxOptions = option.map((row) => ({
      value: row.value,
      label: row.label,
    }));
    setProductSalesTaxes(selectedSalesTaxOptions);
  };

  useEffect(() => {
    if (!isProductLoading && isProductGetSuccess) {
      if (product.productSalesTaxes) {
        const selectedProductSalesTaxOptions = product.productSalesTaxes.map(
          (row) => ({
            value: row.salesTaxId.toString(),
            label: row.salesTax.abbreviation.toString(),
          })
        );
        setProductSalesTaxes(selectedProductSalesTaxOptions);
      }
    }
  }, [isProductLoading]);

  useEffect(() => {
    loadProduct(Number(productId));
    loadSalesTaxSelectOptions();
  }, []);

  useEffect(() => {
    if (!isSalesTaxSelectOptionsLoading && !isProductLoading) {
      if (productSalesTaxes) {
        const selectedSalesTaxOptions = productSalesTaxes.map((row) => ({
          value: row.value,
          label:
            salesTaxSelectOptions.find((o) => o.value === row.value)?.label ??
            '',
        }));

        setProductSalesTaxes(selectedSalesTaxOptions);
      }

      setIsLoading(false);
    }
  }, [
    // isCategorySelectOptionsLoading,
    isSalesTaxSelectOptionsLoading,
    isProductLoading,
  ]);

  if (isLoading) {
    return <Loading />;
  } else {
    return (
      <>
        <CloseButton onClick={onClose} />

        <Box size="medium">
          <Formik
            initialValues={product}
            validationSchema={productValidation}
            validateOnBlur={true}
            validateOnChange={true}
            onSubmit={(values, actions) => {
              if (productSalesTaxes) {
                values.productSalesTaxes = productSalesTaxes.map((row) => ({
                  id: 0,
                  productId: Number(productId),
                  salesTaxId: parseInt(row.value.toString()),
                  salesTax: salesTaxDefaultValue,
                }));
              }
              ProductService.update(Number(productId), values)
                .then((result) => {
                  if (result.isSuccess) {
                    showSuccess('Successfully updated the record.');
                    onClose && onClose(true);
                  } else {
                    console.log(
                      'Error: Failed to update record.',
                      result.errorMessages
                    );
                    showError(`${result.errorMessages}`);
                  }
                })
                .finally(() => {
                  actions.setSubmitting(false);
                });
            }}
          >
            {(formikProps) => {
              return (
                <form method="POST" onSubmit={formikProps.handleSubmit}>
                  <FormSectionContainer>
                    <FormikInput label="Name" name="name" />
                    <CategorySelector
                      selectedValue={formikProps.values.categoryID}
                      onValueChange={(c) =>
                        formikProps.setFieldValue('categoryID', c)
                      }
                    />
                    <FormikInput label="Description" name="description" />
                    <FormikInput label="Price" name="price" />
                    <div className="flex">
                      <FormikCheckbox label="Sell this" name="isSales" />
                      <FormikCheckbox label="Buy This" name="isPurchase" />
                    </div>

                    <FormikSelectMultiple
                      label="Sales Tax"
                      name="productSalesTaxes"
                      selection={salesTaxSelectOptions}
                      selectedOptions={productSalesTaxes}
                      changeHandler={handleProductSalesTaxesOnChange}
                      maxMenuHeight={100}
                    />

                    <Space />
                  </FormSectionContainer>
                  <FormButtonsContainer>
                    <div className="flex space-x-4">
                      <CancelButton onClick={onClose} />

                      <DeleteButton onClick={onDelete} />

                      <SubmitButton
                        label="Save"
                        disabled={
                          formikProps.isSubmitting || !formikProps.isValid
                        }
                      />
                    </div>
                  </FormButtonsContainer>
                </form>
              );
            }}
          </Formik>
        </Box>
      </>
    );
  }
};
